import { FC, createContext, useContext, useMemo } from "react";
import { WithChildren } from "../../../../_metronic/helpers";
import { useQuery } from "react-query";
import { getApplications } from "../applications";
import { filter, remove } from "lodash";
import { getDLMApplications } from "../doclabel";
import { useAuth } from "../../auth";


type AppViewContextProps = {
    cntApplications : number
    cntDLMSiteInspectApplications: number
    cntDLMDOCApplications: number
    cntDLMLabelApplications: number
}

const initialAppView = {
    cntApplications: 0,
    cntDLMSiteInspectApplications: 0,
    cntDLMDOCApplications: 0,
    cntDLMLabelApplications: 0
}


const AppViewContext = createContext<AppViewContextProps>(initialAppView);

const AppViewProvider: FC<WithChildren> = ({children}) => {

    const { currentUser, hasRole } = useAuth();

    const { data: app_data, isLoading: appIsLoading, isError: appIsError } = useQuery(
        ['APPLICATIONS-LIST','own'],
        getApplications,
        {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
    )
  
    const { data: dlm_siteinspect_data } = useQuery(
      ['DLMAPPLICATIONS-LIST', { lastpath: 'siteinspection-applications-list'} ],
      () => getDLMApplications('siteinspection-applications-list'),
      {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
    )

    
    const { data: dlm_docs_data } = useQuery(
      ['DLMAPPLICATIONS-LIST', { lastpath: 'doc-applications-list'} ],
      () => getDLMApplications('doc-applications-list'),
      {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
    )
    
    const { data: dlm_labels_data } = useQuery(
      ['DLMAPPLICATIONS-LIST', { lastpath: 'label-applications-list'} ],
      () => getDLMApplications('label-applications-list'),
      {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
    )

    const cntApplications = useMemo(() => {

        if (appIsLoading || appIsError || app_data === undefined) return 0;
        // count only pending applications
        const { data } = app_data;
        let num = filter(data, (app) => (! (app.state && app.state.match(/(Completed|Failed|Terminated|Draft)/i)))).length;        
        return num;
    }, [app_data, appIsLoading, appIsError]);


    const cntDLMSiteInspectApplications = useMemo(() => {

      if ((! (dlm_siteinspect_data && dlm_siteinspect_data?.data && dlm_siteinspect_data.data.length > 0))) return 0;
  
      let _data = filter(dlm_siteinspect_data.data, (app) => (! (app.state && app.state.match(/(Completed|Failed|Terminated|Draft|Withdrawn)/i))));
      
      if (currentUser && currentUser.isSetsco && hasRole('System Admin')) {
          remove(_data, (o) => (! (o?.assignedTo && o.assignedTo.length > 0 && o.assignedTo[0]?.id && o.assignedTo[0].id === currentUser.id)))    
      }            
      
      return _data.length;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dlm_siteinspect_data, currentUser]);


    const cntDLMDOCApplications = useMemo(() => {

      if ((! (dlm_docs_data && dlm_docs_data?.data && dlm_docs_data.data.length > 0))) return 0;
  
      let _data = filter(dlm_docs_data.data, (app) => (! (app.state && app.state.match(/(Completed|Failed|Terminated|Draft|Withdrawn)/i))));
      
      if (currentUser && currentUser.isSetsco && hasRole('System Admin')) {
          remove(_data, (o) => (! (o?.assignedTo && o.assignedTo.length > 0 && o.assignedTo[0]?.id && o.assignedTo[0].id === currentUser.id)))    
      }            
      
      return _data.length;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dlm_docs_data, currentUser]);
    
    const cntDLMLabelApplications = useMemo(() => {

      if ((! (dlm_labels_data && dlm_labels_data?.data && dlm_labels_data.data.length > 0))) return 0;
  
      let _data = filter(dlm_labels_data.data, (app) => (! (app.state && app.state.match(/(Completed|Failed|Terminated|Draft|Withdrawn)/i))));
      
      if (currentUser && currentUser.isSetsco && hasRole('System Admin')) {
          remove(_data, (o) => (! (o?.assignedTo && o.assignedTo.length > 0 && o.assignedTo[0]?.id && o.assignedTo[0].id === currentUser.id)))    
      }            
      
      return _data.length;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dlm_labels_data, currentUser]);


    return (
    <AppViewContext.Provider value={{cntApplications, cntDLMSiteInspectApplications, cntDLMDOCApplications, cntDLMLabelApplications}}>
        {children}
    </AppViewContext.Provider>
    )
}

const useAppView = () => useContext(AppViewContext);
export {AppViewProvider, useAppView}